import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { DEFAULT_LOCALE, translate } from '@formosa/shared/i18n/translate';
import { journeyApi } from '../../api/journeyApi';
import { journeyBudgetApi } from '../../api/journeyBudgetApi';
import { journeyExpenseApi } from '../../api/journeyExpenseApi';
import { journeySplitApi } from '../../api/journeySplitApi';
import { useAuth } from '../../auth/AuthContext';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { collectMemberIds, memberFallbackLabel } from '../member-display/memberDisplayModel';
import { formatMoneyDisplay } from '../finance/financeMoney';
import {
  CostBalanceHints,
  CostCalculationStatus,
  CostDisclaimer,
} from '../journey-cost';
import { normalizeJourneyBudget } from '../journey-budget/journeyBudgetModel';
import { normalizeExpenseSummary } from '../journey-expense/journeyExpenseModel';
import { WORKSPACE_PRODUCT_CHROME } from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import { ConfirmDialog, ErrorState, LoadingState } from '../../components/ui';
import {
  BALANCE_CHAT_SHARE_CONFIRM,
  buildCalculateSplitPayload,
  canCalculateSplit,
  canShareBalanceToChat,
  classifySplitError,
  createSplitClientRequestId,
  memberDisplayLabel,
  normalizeSplitCalculation,
  normalizeSplitMemberBalances,
} from './journeySplitModel';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';
import '../journey-cost/journey-cost.css';
import './journey-split.css';

export default function JourneySplitPage() {
  const { journeyId } = useParams();
  const { user } = useAuth();
  const t = useCallback((key) => translate(DEFAULT_LOCALE, key), []);
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);
  const calculateRequestIdRef = useRef(null);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [budget, setBudget] = useState(null);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [balances, setBalances] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmCalculate, setConfirmCalculate] = useState(false);
  const [sharingBalance, setSharingBalance] = useState(false);
  const [balanceShareConfirmOpen, setBalanceShareConfirmOpen] = useState(false);
  const [chatShareNotice, setChatShareNotice] = useState('');
  const chatShareLock = useRef(false);
  const memberSummaries = useMemberDisplayNames(collectMemberIds([
    ...(journey?.members || []).map((member) => member.memberId),
    ...(balances?.members || []).map((row) => row.memberId),
    ...(calculation?.memberResults || []).map((row) => row.memberId),
  ]));

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) {
      setPhase('loading');
      setErrorModel(null);
      setConfirmCalculate(false);
    }
    setFormError('');
    try {
      const detailPayload = await journeyApi.getDetail(journeyId);
      if (requestId !== generation.current) return;
      const nextJourney = normalizeJourneyWorkspace(detailPayload, viewerMemberId);
      setJourney(nextJourney);

      let budgetPayload = null;
      try {
        budgetPayload = await journeyBudgetApi.get(journeyId);
      } catch (cause) {
        if (requestId !== generation.current) return;
        if (classifySplitError(cause) === 'budget-not-found') {
          setBudget(null);
          setExpenseSummary(null);
          setCalculation(null);
          setBalances(null);
          setPhase('needs-budget');
          return;
        }
        throw cause;
      }
      if (requestId !== generation.current) return;
      setBudget(normalizeJourneyBudget(budgetPayload));

      const summaryPayload = await journeyExpenseApi.getSummary(journeyId);
      if (requestId !== generation.current) return;
      const nextSummary = normalizeExpenseSummary(summaryPayload);
      setExpenseSummary(nextSummary);

      let nextCalculation = null;
      let nextBalances = null;
      try {
        const [calcPayload, balancePayload] = await Promise.all([
          journeySplitApi.getLatest(journeyId),
          journeySplitApi.getMemberBalances(journeyId),
        ]);
        if (requestId !== generation.current) return;
        nextCalculation = normalizeSplitCalculation(calcPayload);
        nextBalances = normalizeSplitMemberBalances(balancePayload);
      } catch (cause) {
        if (requestId !== generation.current) return;
        const kind = classifySplitError(cause);
        if (kind !== 'split-not-found') throw cause;
      }
      setCalculation(nextCalculation);
      setBalances(nextBalances);

      if (!nextCalculation && (nextSummary.activeExpenseCount || 0) === 0) {
        setPhase('needs-expenses');
      } else if (!nextCalculation) {
        setPhase('empty');
      } else {
        setPhase('ready');
      }
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifySplitError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'journey-not-found' || access === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || access === 'forbidden') setPhase('forbidden');
      else {
        setErrorModel(mapApiErrorToUiModel(cause, { t }));
        setPhase('error');
      }
    }
  }, [journeyId, t, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  const canCalculate = canCalculateSplit({
    role: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
  }) && Boolean(budget);
  const canShareBalance = canShareBalanceToChat({
    role: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
  }) && Boolean(balances);

  function memberLabel(memberId) {
    const member = (journey?.members || []).find((row) => String(row.memberId) === String(memberId));
    return member
      ? memberDisplayLabel(member, memberSummaries)
      : memberFallbackLabel(memberId);
  }

  function mapMutationError(cause) {
    const kind = classifySplitError(cause);
    if (kind === 'conflict') {
      return { kind, message: '旅行財務資料已更新，請重新載入後再計算。' };
    }
    if (kind === 'idempotency-conflict') {
      return { kind, message: '請求識別碼衝突，請重新開始計算。' };
    }
    if (kind === 'validation') {
      return { kind, message: '請檢查分攤計算輸入。' };
    }
    if (kind === 'forbidden') {
      return { kind, message: '你沒有權限計算分帳。' };
    }
    if (kind === 'lifecycle') {
      return { kind, message: '目前行程狀態無法計算分帳。' };
    }
    if (kind === 'budget-not-found') {
      return { kind, message: '請先設定旅行預算' };
    }
    return {
      kind,
      message: resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed') || '分攤計算失敗。',
    };
  }

  async function runCalculate() {
    if (mutating.current || !budget || !canCalculate) return;
    mutating.current = true;
    setBusy(true);
    setFormError('');
    const requestId = generation.current;
    try {
      if (!calculateRequestIdRef.current) {
        calculateRequestIdRef.current = createSplitClientRequestId();
      }
      await journeySplitApi.calculate(
        journey.journeyId,
        buildCalculateSplitPayload({
          expectedBudgetRowVersion: budget.rowVersion,
          clientRequestId: calculateRequestIdRef.current,
        }),
      );
      if (requestId !== generation.current) return;
      calculateRequestIdRef.current = null;
      setConfirmCalculate(false);
      await load({ soft: true });
    } catch (cause) {
      if (requestId !== generation.current) return;
      const mapped = mapMutationError(cause);
      setFormError(mapped.message);
      setConfirmCalculate(false);
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  function requestShareBalanceToChat() {
    if (chatShareLock.current || !canShareBalance) return;
    setBalanceShareConfirmOpen(true);
  }

  async function confirmShareBalanceToChat() {
    if (chatShareLock.current || !canShareBalance) return;
    chatShareLock.current = true;
    setSharingBalance(true);
    setChatShareNotice('');
    setFormError('');
    try {
      await journeySplitApi.shareBalanceToChat(journey.journeyId, { confirm: true });
      setChatShareNotice('目前分帳摘要已分享到旅程聊天室');
      setBalanceShareConfirmOpen(false);
    } catch (cause) {
      const kind = classifySplitError(cause);
      if (kind === 'forbidden') setFormError('你沒有權限分享分帳摘要。');
      else if (kind === 'unavailable') setFormError('聊天室暫時無法使用。');
      else if (kind === 'split-not-found') setFormError('尚未計算分帳，無法分享。');
      else {
        setFormError(
          resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed')
            || '分享到旅程聊天室失敗。',
        );
      }
    } finally {
      chatShareLock.current = false;
      setSharingBalance(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label={t('travel.common.loading')} data-testid="split-loading" />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.notFoundTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.notFoundBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.forbiddenTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.forbiddenBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = resolveUiErrorCopy(errorModel, t);
    return (
      <main className="journey-workspace-state" data-testid="split-error">
        <ErrorState
          title="無法開啟分帳"
          description={errorCopy.description}
          retryable
          retryLabel="重試"
          onRetry={load}
        />
      </main>
    );
  }

  const members = balances?.members || calculation?.memberResults || [];
  const statusSource = balances || calculation;
  const currency = statusSource?.baseCurrency || budget?.baseCurrency || '';
  const outdated = Boolean(statusSource?.isOutdated);
  const showCalculateCta = canCalculate && (phase === 'empty' || phase === 'ready');
  const calcStatus = phase === 'empty'
    ? ''
    : (statusSource?.status || '');

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey?.title || 'Journey'}</h1>
          <p className="journey-workspace__meta">
            {journey?.lifecycleStatusLabel}
            {journey?.requesterRoleLabel ? ` · ${journey.requesterRoleLabel}` : ''}
          </p>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel journey-split" aria-labelledby="split-heading">
        <div className="journey-workspace__panel-head">
          <h2 id="split-heading">分帳</h2>
        </div>

        {phase === 'needs-budget' && (
          <div className="journey-split__empty" role="status">
            <p>請先設定旅行預算</p>
            <Link to={`/journeys/${journey.journeyId}/budget`}>前往預算</Link>
          </div>
        )}

        {phase === 'needs-expenses' && (
          <div className="journey-split__empty" role="status">
            <p>尚無可分攤支出</p>
            <p className="journey-split__hint">請先在支出頁記錄至少一筆有效支出。</p>
            <Link to={`/journeys/${journey.journeyId}/expenses`}>前往支出</Link>
          </div>
        )}

        {(phase === 'empty' || phase === 'ready') && (
          <>
            <CostDisclaimer />

            {phase === 'empty' && (
              <div className="journey-split__empty" role="status">
                <p>尚未計算分帳</p>
                <p className="journey-split__hint">
                  依目前有效支出計算每位旅伴的分攤結果。
                </p>
              </div>
            )}

            <CostCalculationStatus
              status={calcStatus}
              detail={outdated
                ? '此結果基於較早的支出資料。支出已更新，請重新計算。'
                : undefined}
            />

            {statusSource && (
              <dl className="journey-split__summary">
                <div>
                  <dt>進行中支出合計</dt>
                  <dd>
                    {formatMoneyDisplay(statusSource.totalActiveExpenseAmount)}
                    {currency ? ` ${currency}` : ''}
                  </dd>
                </div>
                <div>
                  <dt>進行中筆數</dt>
                  <dd>{calculation?.activeExpenseCount ?? expenseSummary?.activeExpenseCount ?? '—'}</dd>
                </div>
                <div>
                  <dt>計算時間</dt>
                  <dd>{statusSource.calculatedAt || '—'}</dd>
                </div>
                <div>
                  <dt>幣別</dt>
                  <dd>{currency || '—'}</dd>
                </div>
              </dl>
            )}

            {!canCalculate && (
              <p className="journey-split__readonly">
                你可以查看分帳結果，但目前角色或行程狀態無法重新計算。
              </p>
            )}

            {formError && (
              <p className="journey-workspace__error" role="alert">
                {formError}
                {formError.includes('重新載入') && (
                  <>
                    {' '}
                    <button type="button" onClick={() => load()}>重新載入</button>
                  </>
                )}
              </p>
            )}

            {chatShareNotice && (
              <p className="journey-workspace__notice" role="status">
                {chatShareNotice}
                {' '}
                <Link to={`/journeys/${journey.journeyId}/chat`}>查看聊天室</Link>
              </p>
            )}

            {canShareBalance && (
              <div className="journey-split__actions">
                <button
                  type="button"
                  onClick={requestShareBalanceToChat}
                  disabled={busy || sharingBalance}
                >
                  {sharingBalance ? '分享中…' : '分享分帳摘要到旅程聊天室'}
                </button>
              </div>
            )}

            {showCalculateCta && (
              <div className="journey-split__actions">
                {confirmCalculate ? (
                  <div className="journey-split__confirm" role="status">
                    <div className="journey-cost-confirm-panel" data-testid="split-confirm-panel">
                      <p>
                        <strong>請確認你了解：這不是付款流程。</strong>
                      </p>
                      <p>此功能僅協助記帳與分攤，不代表平台已代為付款。</p>
                      <p>
                        {outdated || calculation
                          ? '確認依目前進行中的支出重新計算分帳？'
                          : '確認依目前進行中的支出計算分帳？'}
                      </p>
                    </div>
                    <div className="journey-split__actions">
                      <button
                        type="button"
                        className="journey-split__confirm-primary"
                        onClick={runCalculate}
                        disabled={busy}
                      >
                        確認
                      </button>
                      <button type="button" onClick={() => setConfirmCalculate(false)} disabled={busy}>
                        取消
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setFormError('');
                      setConfirmCalculate(true);
                    }}
                    disabled={busy}
                  >
                    {outdated || calculation ? '重新計算' : '計算分帳'}
                  </button>
                )}
              </div>
            )}

            {members.length > 0 ? (
              <CostBalanceHints
                members={members}
                currency={currency}
                memberLabel={memberLabel}
              />
            ) : null}

            {statusSource && members.length === 0 && (
              <p className="journey-split__hint">此計算快照沒有成員餘額列。</p>
            )}
          </>
        )}
      </section>

      <ConfirmDialog
        open={balanceShareConfirmOpen}
        title="分享分帳摘要到旅程聊天室"
        description={BALANCE_CHAT_SHARE_CONFIRM}
        confirmLabel="分享到聊天室"
        cancelLabel={WORKSPACE_PRODUCT_CHROME.cancel}
        loading={sharingBalance}
        onCancel={() => {
          if (!sharingBalance) setBalanceShareConfirmOpen(false);
        }}
        onConfirm={confirmShareBalanceToChat}
      />
    </main>
  );
}
